import GameEvents from "../../src/const/GameEvents";
import App from "../App";
import Assets from "../utils/Assets";
import ReelGroup from "./ReelGroup";
import ReelSymbol from "./Symbol";
import GameFactory from "../factories/BaseGameFactory";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import Layout from "../utils/Layout";
import SceneView from "../views/Symbol";
import AbstractReelBehaviour from "../spinners/AbstractReelBehaviour";
import { utils as PixiUtils, Container, Graphics } from "pixi.js";

/**
 * Abstraction for a single reel (a vertical slice of symbols).
 * It's not a visual component (container) , but serves as a logical entity in the game structure
 */
class Reel extends PixiUtils.EventEmitter {
    public static yCompFunc(s1: ReelSymbol, s2: ReelSymbol) {
        return s1.y - s2.y;
    }

    public static yCompFuncReverse(s1: ReelSymbol, s2: ReelSymbol) {
        return s2.y - s1.y;
    }

    public static xCompFunc(s1: ReelSymbol, s2: ReelSymbol) {
        return s1.x - s2.x;
    }

    public static xCompFuncReverse(s1: ReelSymbol, s2: ReelSymbol) {
        return s2.x - s1.x;
    }

    public reelIndex: number;
    public numSyms: number;
    public yOrigin: number;
    public xOrigin: number;
    public symbols: ReelSymbol[];
    public symbolsPositions: {x:number,y:number}[];
    public symStep: number;
    public isHeld: boolean;
    public reelBottom: number;
    public reelHeight: number;
    public symBounds: IObjectConfig;
    public spinner: AbstractReelBehaviour;
    public symbolWrapDistance: number;
    public symContainer: Container;
    public _symContainer: Container;
    public mask: Graphics;
    protected config: any;
    protected extraSymbols: number;
    protected assets: Assets;
    protected gameFactory;
    protected orientation: ReelOrientation;
    protected cfg: {
        symBounds: IObjectConfig;
        rowSeparator: IObjectConfig;
        reelBounds: IObjectConfig;
        reelSeparator: IObjectConfig;
        extraSymbols: number;
        sceneName?: string;
    };

    constructor(symContainer: Container, reelIndex: number) {
        super();

        this.config = App.config;
        this.assets = App.assets;
        this.gameFactory = App.gameFactory;
        this.symContainer = symContainer;
        // this.symContainer = new Container;
        // symContainer.addChild(this.symContainer);
        this.reelIndex = reelIndex;
    }

    public getExtraSymbolsCount(): number {
        return this.extraSymbols;
    }

    public create(
        symbolData: any,
        config: {
            symBounds: IObjectConfig;
            rowSeparator: IObjectConfig;
            reelBounds: IObjectConfig;
            reelSeparator: IObjectConfig;
            extraSymbols: number;
            sceneName?: string;
        },
        positionDefinitions: Map<string, IObjectConfig>,
        forcedOrientation?: ReelOrientation
    ) {
        const cfg = config;
        this.cfg = cfg;
        this.symBounds = cfg.symBounds;
        this.extraSymbols = cfg.extraSymbols;
        if (forcedOrientation == undefined) {
            this.orientation =
            cfg.reelBounds.width <= cfg.reelBounds.height
                ? ReelOrientation.Vertical
                : ReelOrientation.Horizontal;
        } else {
            this.orientation = forcedOrientation;
        }

        switch (this.orientation) {
            case ReelOrientation.Vertical: {
                this.numSyms = Math.floor(
                    cfg.reelBounds.height / config.symBounds.height
                );
                break;
            }
            case ReelOrientation.Horizontal: {
                this.numSyms = Math.floor(
                    config.reelBounds.width / config.symBounds.width
                );
                break;
            }
            default:
                break;
        }

        // +2 : one above and one below the visible reels
        const numSyms = this.numSyms + cfg.extraSymbols * 2;
        // Layout.setPos(this.symContainer,cfg.reelBounds.x,cfg.reelBounds.y)
        const xOrigin = Layout.scalePixels(
            // Horizontal origin should be the middle of the reel (not the symbol)
            cfg.reelBounds.x + cfg.reelBounds.width * 0.5
        );
        const yOrigin = Layout.scalePixels(
            cfg.reelBounds.y +  cfg.symBounds.height * 0.5
        );

        this.symbols = [];
        this.symbolsPositions = [];
        const offsetX = cfg.reelSeparator ? cfg.reelSeparator.width : 0;
        const offsetY = cfg.rowSeparator ? cfg.rowSeparator.height : 0;
        const offset = (numSyms - symbolData.length) / 2;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            const s = this.gameFactory.getClass("Symbol") as ReelSymbol;
            s.create(cfg.symBounds, positionDefinitions);
            this.symContainer.addChild(s);
            s.reelIdx = this.reelIndex;
            s.x =
                xOrigin +
                Layout.scalePixels(
                    (cfg.symBounds.width + offsetX) *
                        ((this.orientation - 1) * cfg.extraSymbols +
                            symIdx * (1 - this.orientation))
                );
            s.y =
                yOrigin +
                Layout.scalePixels(
                    (cfg.symBounds.height + offsetY) *
                        (0 -
                            this.orientation * cfg.extraSymbols +
                            symIdx * this.orientation)
                );

            if (symbolData.length > 0 && symIdx >= offset) {
                s.setId(symbolData.shift());
            } else {
                s.setId(1);
            }

            s.isFinal = symIdx >= cfg.extraSymbols && symIdx < numSyms - cfg.extraSymbols;
            if(!s.isFinal) {
                s.alpha = 0;
            }

            this.symbols.push(s);
            this.symbolsPositions.push({
                x:s.x,
                y:s.y
            });
        }

        switch (this.orientation) {
            case ReelOrientation.Vertical: {
                this.symStep = Layout.scalePixels(
                    cfg.symBounds.width + offsetX
                );
                break;
            }
            case ReelOrientation.Horizontal: {
                this.symStep = Layout.scalePixels(
                    cfg.symBounds.height + offsetY
                );
                break;
            }
            default:
                break;
        }

        // -Layout.scalePixels(cfg.reelBounds.y) to compensate for reel position
        this.yOrigin = Layout.scalePixels(cfg.symBounds.height * 0.5) - cfg.extraSymbols * this.symStep -Layout.scalePixels(cfg.reelBounds.y);
        // TODO this may need fixing
        this.xOrigin = Layout.scalePixels(cfg.reelBounds.x);


        this.reelBottom = yOrigin + (numSyms - cfg.extraSymbols) * this.symStep;
        this.reelHeight = yOrigin + (numSyms - cfg.extraSymbols * 2 ) * this.symStep;
        this.symbolWrapDistance = numSyms * this.symStep;
    }

    public calculateStopDistance(symCount:number) {
        return symCount * this.symStep;
    }
    
    public getOriginOffset():number {
         return Layout.scalePixels(
            this.cfg.reelBounds.y 
        );
    }

    public maskReel(mask?: Graphics) {
        if (mask) {
            if(this.mask === mask) {
                return;
            }
            this.mask = mask;
        } else {
            if (!this.mask) {
                this.mask = new Graphics();
                this.mask.beginFill(0x000000, 0.01);
                this.mask.drawRect(
                    0,
                    0,
                    Layout.scalePixels(this.cfg.reelBounds.width),
                    Layout.scalePixels(this.cfg.reelBounds.height)
                );
                this.mask.endFill();
                this.symContainer.addChild(this.mask);
            } else {
                Layout.testSetWidth(this.mask, this.cfg.reelBounds.width, false);
                Layout.testSetHeight(this.mask, this.cfg.reelBounds.height, false);
            }
            this.mask.x = Layout.scalePixels(this.cfg.reelBounds.x);
            this.mask.y = Layout.scalePixels(this.cfg.reelBounds.y);
        }
    }

    public removeMask() {
        this.mask = null;
    }

    public getOrientation(): ReelOrientation {
        return this.orientation;
    }

    public unmaskSymbols() {
        const numSyms = this.symbols.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            const s = this.symbols[symIdx];
            // s.hideWin();
            // s.isFinal = false;
            s.mask = null;

            // if (
            //     symIdx === 0 ||
            //     symIdx === numSyms - this.extraSymbols ||
            //     App.config.disableUnmasking
            // ) {
            //     s.alpha = 1;
            // }
        }
    }

    public resetSymbolsForSpin() {
        const numSyms = this.symbols.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            const s = this.symbols[symIdx];
            s.hideWin();
            if (!App.instance.debugBounds) {
                s.hideDebugTag();
            }

            s.isFinal = false;

            if (
                symIdx === 0 ||
                symIdx === numSyms - this.extraSymbols ||
                App.config.disableUnmasking
            ) {
                s.alpha = 1;
            }
        }
    }

    public sortSymbolsByY() {
        this.symbols.sort(Reel.yCompFunc);
    }

    public sortSymbolsByX() {
        this.symbols.sort(Reel.xCompFunc);
    }

    public hideSymbols() {
        for (let i = 0; i < this.symbols.length; i++) {
            this.symbols[i].hide();
        }
    }

    public showSymbols() {
        for (let i = 0; i < this.symbols.length; i++) {
            this.symbols[i].show();
        }
    }
}

export enum ReelOrientation {
    Horizontal = 0,
    Vertical = 1,
}

export default Reel;
